import type { Vector3 } from "three";

/**
 * A single resolved surface drag area (one panel of a box side).
 */
export interface ResolvedSurfaceArea {
  bounds: { min: Vector3; max: Vector3 };
  /** In-plane center of this panel, for per-area center snapping. */
  center?: Vector3;
}

/**
 * Constrains a point to a surface's movement area.
 *
 * The incoming `point` already lies on the drag plane that was built through the
 * part's real position (with the surface normal) at pointer-down, so its
 * surface-normal coordinate is the authoritative one the build uses. We must NOT
 * re-project it onto a bounds-derived plane or clamp the normal axis: the
 * constraint bounds come from the panel's inner face (`innerCenter[normal]`)
 * while the build places the part on the outer face (`outerCenter[normal]`), and
 * forcing the normal coordinate would shove the part through the panel by its
 * thickness (jump on drag start, jump back on reconcile). Instead we keep the
 * point's normal coordinate and clamp only the two in-plane axes to the bounds.
 */
export function projectOntoSurface(
  point: Vector3,
  planeNormal: Vector3,
  bounds: { min: Vector3; max: Vector3 },
): Vector3 {
  const clampAxis = (value: number, min: number, max: number) =>
    Math.max(Math.min(min, max), Math.min(Math.max(min, max), value));

  const projected = point.clone();
  // Only clamp axes that lie in the surface plane (normal component ~0); leave
  // the surface-normal axis at the point's own (correct) value.
  if (Math.abs(planeNormal.x) < 0.9) {
    projected.x = clampAxis(projected.x, bounds.min.x, bounds.max.x);
  }
  if (Math.abs(planeNormal.y) < 0.9) {
    projected.y = clampAxis(projected.y, bounds.min.y, bounds.max.y);
  }
  if (Math.abs(planeNormal.z) < 0.9) {
    projected.z = clampAxis(projected.z, bounds.min.z, bounds.max.z);
  }

  return projected;
}

/**
 * True when `point`'s in-plane coordinates lie inside `bounds`. The
 * surface-normal axis is ignored (same rule as {@link projectOntoSurface}).
 */
export function isPointInsideSurfaceBounds(
  point: Vector3,
  planeNormal: Vector3,
  bounds: { min: Vector3; max: Vector3 },
): boolean {
  if (Math.abs(planeNormal.x) < 0.9) {
    if (point.x < bounds.min.x || point.x > bounds.max.x) {
      return false;
    }
  }
  if (Math.abs(planeNormal.y) < 0.9) {
    if (point.y < bounds.min.y || point.y > bounds.max.y) {
      return false;
    }
  }
  if (Math.abs(planeNormal.z) < 0.9) {
    if (point.z < bounds.min.z || point.z > bounds.max.z) {
      return false;
    }
  }
  return true;
}

/**
 * Whether snapping `point` along `axis` onto `center` would keep it inside the
 * active panel bounds (multi-area surfaces with global center snapping).
 */
export function canAxisCenterSnapStayInBounds(
  point: Vector3,
  axis: Vector3,
  center: Vector3,
  planeNormal: Vector3,
  bounds: { min: Vector3; max: Vector3 },
): boolean {
  const distToCenter = point.clone().sub(center).dot(axis);
  const trial = point.clone().addScaledVector(axis, -distToCenter);
  return isPointInsideSurfaceBounds(trial, planeNormal, bounds);
}

/**
 * Projects a point onto the nearest of several surface areas (panels). Each
 * area is clamped independently (in-plane only, normal coord preserved); the
 * area whose clamp is closest to the raw point wins. This keeps the part on a
 * valid panel and makes it "jump" across the gap to the next panel once the
 * pointer passes the midpoint of the gap.
 */
export function projectOntoSurfaceAreas(
  point: Vector3,
  planeNormal: Vector3,
  areas: ResolvedSurfaceArea[],
): { position: Vector3; index: number } {
  let bestPos: Vector3 | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  let bestIdx = -1;
  areas.forEach((area, i) => {
    const projected = projectOntoSurface(point, planeNormal, area.bounds);
    const dist = point.distanceTo(projected);
    if (dist < bestDist) {
      bestDist = dist;
      bestPos = projected;
      bestIdx = i;
    }
  });
  return { position: bestPos ?? point.clone(), index: bestIdx };
}

/**
 * Builds the line-segment vertex list (pairs of points) outlining a rectangle
 * on an axis-aligned surface plane, keeping the surface-normal coordinate fixed
 * at the box center on the normal axis.
 */
export function surfaceRectSegments(
  min: Vector3,
  max: Vector3,
  planeNormal: Vector3,
): number[] {
  if (Math.abs(planeNormal.x) > 0.9) {
    const x = (min.x + max.x) / 2;
    return [
      x,
      min.y,
      min.z,
      x,
      max.y,
      min.z,
      x,
      max.y,
      min.z,
      x,
      max.y,
      max.z,
      x,
      max.y,
      max.z,
      x,
      min.y,
      max.z,
      x,
      min.y,
      max.z,
      x,
      min.y,
      min.z,
    ];
  }
  if (Math.abs(planeNormal.y) > 0.9) {
    const y = (min.y + max.y) / 2;
    return [
      min.x,
      y,
      min.z,
      max.x,
      y,
      min.z,
      max.x,
      y,
      min.z,
      max.x,
      y,
      max.z,
      max.x,
      y,
      max.z,
      min.x,
      y,
      max.z,
      min.x,
      y,
      max.z,
      min.x,
      y,
      min.z,
    ];
  }
  const z = (min.z + max.z) / 2;
  return [
    min.x,
    min.y,
    z,
    max.x,
    min.y,
    z,
    max.x,
    min.y,
    z,
    max.x,
    max.y,
    z,
    max.x,
    max.y,
    z,
    min.x,
    max.y,
    z,
    min.x,
    max.y,
    z,
    min.x,
    min.y,
    z,
  ];
}

/** Default in-drag grid step (mm) while Ctrl/Cmd is held without Shift. */
export const DEFAULT_DRAG_GRID_STEP_MM = 10;

/** Fine in-drag grid step (mm) while Ctrl/Cmd+Shift is held. */
export const FINE_DRAG_GRID_STEP_MM = 1;

/**
 * Rounds a position along each draggable axis to the nearest grid step. Axes
 * must be unit vectors for independent degrees of freedom (orthogonal in-plane
 * axes for surfaces, edge direction for 1D drags). Components not aligned with
 * any axis are unchanged.
 */
export function snapPositionToDragGrid(
  position: Vector3,
  axes: Vector3[],
  stepMm: number,
): Vector3 {
  if (stepMm <= 0 || axes.length === 0) {
    return position.clone();
  }
  const snapped = position.clone();
  for (const axis of axes) {
    const coord = snapped.dot(axis);
    const rounded = Math.round(coord / stepMm) * stepMm;
    snapped.addScaledVector(axis, rounded - coord);
  }
  return snapped;
}

/** Grid step from pointer modifiers: Shift = 1 mm, otherwise 10 mm. */
export function dragGridStepFromModifiers(shiftKey: boolean): number {
  return shiftKey ? FINE_DRAG_GRID_STEP_MM : DEFAULT_DRAG_GRID_STEP_MM;
}
